import { Suspense, lazy } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import LibraryPage from '@/features/library/pages/LibraryPage';
import BookPage from '@/features/library/pages/BookPage';
import WalletPage from '@/features/wallet/pages/WalletPage';
import SettingsPage from '@/features/settings/pages/SettingsPage';
import DeveloperTokensPage from '@/features/settings/pages/DeveloperTokensPage';
import DeleteAccountPage from '@/features/settings/pages/DeleteAccountPage';
import ContentPage from '@/features/content/pages/ContentPage';
import PublicSharePage from '@/features/sharing/pages/PublicSharePage';
import SharedVideoPage from '@/features/sharing/pages/SharedVideoPage';
import AdminUsersPage from '@/features/admin/pages/AdminUsersPage';
import AdminCreditGrantsPage from '@/features/admin/pages/AdminCreditGrantsPage';
import AdminFeedbackPage from '@/features/admin/pages/AdminFeedbackPage';
import AdminUserJobsPage from '@/features/admin/pages/AdminUserJobsPage';
import AdminFeaturedPage from '@/features/admin/pages/AdminFeaturedPage';
import AdminSliPage from '@/features/admin/pages/AdminSliPage';
import AdminQualityPage from '@/features/admin/pages/AdminQualityPage';
import NotFoundPage from '@/app/routing/NotFoundPage';
import LoginPage from '@/features/auth/pages/LoginPage';
import AuthCallbackPage from '@/features/auth/pages/AuthCallbackPage';
import AccountPage from '@/features/account/pages/AccountPage';
import StatusPage from '@/features/status/pages/StatusPage';
import RootRoute from '@/app/routing/RootRoute';
import LandingPage from '@/features/landing/pages/LandingPage';
import ShowcaseStyleRoute from '@/app/routing/ShowcaseStyleRoute';
import { AppLayout } from '@/app/layout/AppLayout';
import { AuthLayout } from '@/app/layout/AuthLayout';
import { RequireUser } from '@/app/routing/RequireUser';
import { RequireAdmin } from '@/app/routing/RequireAdmin';
import { useNotifications } from '@/app/providers/notifications';
import { useVersionCheck } from '@/shared/hooks/useVersionCheck';

/*
 * The /ui gallery, kept out of the production graph entirely.
 *
 * It used to be a plain top-level import rendered behind `import.meta.env.DEV`.
 * That tree-shook the component — `UiGallery` appears nowhere in the built JS —
 * but not its stylesheet: Vite collects CSS from the static module graph while
 * transforming, long before Rollup decides which JS is reachable, so
 * `gallery.css` went out in the production bundle for a route no user can open.
 *
 * A dynamic import inside a statically-false ternary leaves the graph as well as
 * the bundle: with DEV folded to `false` the `import()` is unreachable and
 * Rollup emits no chunk for it. Verified by grepping dist for `gallery__`.
 */
const UiGalleryRoute = import.meta.env.DEV
  ? lazy(() => import('@/dev/UiGalleryPage'))
  : null;

/**
 * Routing for the Iteration 2.0 information architecture.
 *
 * The top nav is gone; navigation lives in the sidebar rail, and the shell is
 * chosen by nesting rather than by testing the pathname. `RequireUser` guards
 * the whole authenticated layout instead of being repeated per route.
 *
 * Remap from the old IA:
 *   /              was the featured feed, is now the prompt / new-content page
 *   /home          the featured feed, under the name production always used
 *                  (briefly lived at /search, which now redirects here)
 *   /library       unchanged path, gaining shelves
 *   /videos/:id    unchanged path, becoming the content page
 *   /settings      unchanged path; reached from the sidebar user menu now that
 *                  there is no top nav to hold a Settings link
 *   /admin/*       unchanged paths, moved inside the shell — see below
 *   /submission    retired; redirects to `/`, which now holds the prompt box
 */
function App() {
  const { pushNotification } = useNotifications();

  // Long-lived tabs keep their loaded bundle across deploys; offer a refresh
  // instead of letting them run stale code indefinitely.
  useVersionCheck(() =>
    pushNotification({
      message: 'A new version of Valoi is available.',
      actionLabel: 'Refresh',
      onAction: () => window.location.reload(),
    }),
  );

  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
      </Route>

      {/* Public status page: no auth, no app shell, and deliberately not linked
          from the sidebar or anywhere else in the product — reachable only by
          navigating here directly. */}
      <Route path="/status" element={<StatusPage />} />

      {/* `/` is the one path that is public AND private, so it is lifted out of
          the guarded group below and branches inside RootRoute instead: PromptPage
          for a signed-in user (wrapped here in the same AppLayout the guarded
          routes get, so the rail and content card are identical), and the public
          landing page for everyone else.

          Deliberately NOT moved to /prompt to make room for a public root. Six
          places treat `/` as the app root — the sidebar's "New content" link, the
          post-login redirect, returnTo's default, RequireAdmin's fallback — and
          App.routing.test.tsx asserts it. Branching costs one component; moving
          would touch all of them to gain nothing a visitor can see. */}
      <Route path="/" element={<RootRoute />} />

      {/* Public, like `/`: where a showcase band's "See all" leads. Outside the
          guarded group because a visitor with no account is exactly who it is for,
          and it draws its own shell (rail + content) the same way RootRoute's
          signed-out branch does. */}
      <Route path="/templates" element={<ShowcaseStyleRoute index />} />
      <Route path="/showcase/:style" element={<ShowcaseStyleRoute />} />
      <Route path="/share/:token" element={<PublicSharePage />} />

      <Route
        element={
          <RequireUser>
            <AppLayout />
          </RequireUser>
        }
      >
        {/* Home is the catalogue again, and this reverses an earlier decision here.

            It was a redirect to `/`, on the reasoning that the public landing page
            already shows the catalogue to everyone before they sign in, so a signed-in
            copy would be a page they had just scrolled. What that missed is that a
            signed-in user has no route back to it at all: `/` is the composer once you
            are authenticated, so the featured videos became unreachable the moment you
            logged in.

            It renders the same `LandingPage`, not a copy of it -- see its `signedIn`
            prop. Outside AppLayout's rail-and-card shell for the same reason RootRoute's
            public branch is: the page owns its own top bar and viewport. */}
        <Route path="/home" element={<LandingPage signedIn />} />
        <Route path="/library" element={<LibraryPage />} />
        <Route path="/library/books/:bookId" element={<BookPage />} />
        <Route path="/videos/:id" element={<ContentPage />} />
        <Route path="/shares/direct/:shareId" element={<SharedVideoPage />} />
        <Route path="/wallet" element={<WalletPage />} />
        <Route path="/account" element={<AccountPage />} />

        {/* Settings has its own section nav and renders children through an
            Outlet, so it stays a nested route — but the per-route RequireUser
            it arrived with is dropped: this whole group is already guarded, and
            two guards racing produce two redirects. */}
        <Route path="/settings" element={<SettingsPage />}>
          <Route index element={<Navigate to="/settings/tokens" replace />} />
          <Route path="tokens" element={<DeveloperTokensPage />} />
          <Route path="delete" element={<DeleteAccountPage />} />
        </Route>

        {/* The admin dashboard, nested inside the shell rather than beside it.
            On the old IA it was a top-level sibling that drew its own header;
            here it inherits the rail like every other signed-in destination, so
            an admin who lands on `/admin` still has a way back to the app.

            `RequireAdmin` sits inside the group's `RequireUser` rather than
            wrapping it, which is what its own comment asks for: it does not
            handle the loading or unauthenticated states, only the role. Both
            are still UI-level — `require_admin_user` on every admin call is the
            enforcement that counts. */}
        <Route
          element={
            <RequireAdmin>
              <Outlet />
            </RequireAdmin>
          }
        >
          <Route path="/admin" element={<AdminUsersPage />} />
          <Route path="/admin/grants" element={<AdminCreditGrantsPage />} />
          <Route path="/admin/feedback" element={<AdminFeedbackPage />} />
          <Route path="/admin/featured" element={<AdminFeaturedPage />} />
          <Route path="/admin/users/:userId/jobs" element={<AdminUserJobsPage />} />
          <Route path="/admin/sli" element={<AdminSliPage />} />
          <Route path="/admin/quality" element={<AdminQualityPage />} />
        </Route>

        {/* The prompt page submits, so the old submission page is gone. The path
            stays as a redirect rather than a 404: it was the only way to start a
            video for the whole of this branch's life, so it is in bookmarks and
            in muscle memory. */}
        <Route path="/submission" element={<Navigate to="/" replace />} />

        {/* Points at `/home`, not `/`. `/search` was the featured feed's path for
            this branch's whole life, so it is in bookmarks; it should land on the
            feed, and the feed is `/home` again. It briefly pointed at `/` during the
            window when the catalogue existed only as the public landing page and had
            no signed-in route to send anyone to. */}
        <Route path="/search" element={<Navigate to="/home" replace />} />

        {/* Inside the shell, not beside it. As a top-level sibling this rendered
            a signed-in user's mistyped URL as a bare page: no rail, no
            NotificationCenter, no way back but the browser's own button. Nested
            here it keeps the shell, so a wrong turn is recoverable. */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* Dev-only reference for the shared primitive layer. */}
      {UiGalleryRoute && (
        <Route
          path="/ui"
          element={
            <Suspense fallback={null}>
              <UiGalleryRoute />
            </Suspense>
          }
        />
      )}
    </Routes>
  );
}

export default App;
